<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref, defineAsyncComponent, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useCategoriesStore } from '@/stores/CategoriesStore'
import { useAppStore } from '@/stores/AppStore'
import { getCurrentLocaleFlag, LanguagesFlags } from '@/components/svg/flags/languagesFlags'
import { useCartStore } from '@/stores/CartStore'
import ApiServiceV1 from '@/api/adminApi/AdminApiV1'
import { useCountry } from '@/mixins/useCountry'

import Logo from './MixitLogo.vue'
import SpecialCategories from './SpecialCategories.vue'
import StarsSvg from '@/components/svg/navbarMenu/StarsSvg.vue'
import NavSearch from './NavSearch.vue'
import { useModalStore } from '@/stores/ModalStore'

const BaseOdometer = defineAsyncComponent(() => import('@/components/general/BaseOdometer.vue'))
const NavProducts = defineAsyncComponent(() => import('./menus/NavProducts.vue'))
const NavOwnMix = defineAsyncComponent(() => import('./menus/NavOwnMix.vue'))
const NavAppDownload = defineAsyncComponent(() => import('./menus/NavAppDownload.vue'))
const NavMenuMore = defineAsyncComponent(() => import('./menus/NavMenuMore.vue'))
const NavProfile = defineAsyncComponent(() => import('./menus/NavProfile.vue'))
const NavCart = defineAsyncComponent(() => import('./menus/NavCart.vue'))
const NotificationDrawer = defineAsyncComponent(() => import('./NotificationDrawer.vue'))
const FreeTransportAlert = defineAsyncComponent(() => import('./FreeTransportAlert.vue'))

const country = useCountry()
const { t, locale } = useI18n()
const router = useRouter()
const { openModal } = useModalStore()
const categoriesStore = useCategoriesStore()
const { getEventCategories } = storeToRefs(categoriesStore)
const appStore = useAppStore()
const { isSignedIn, getCurrentCustomer, isFreeTransportNotice } = storeToRefs(appStore)
const cartStore = useCartStore()
const { isCartNotEmpty, getTotalItemsInCart, getCartPrice, hasFreeTransport } = storeToRefs(cartStore)

const freeDeliveryMsgShow = ref(true)

const freeShippingMsgHandler = () => { freeDeliveryMsgShow.value = false }

defineProps({
  isTouchScreen: {
    type: Boolean,
    required: true,
  }
})

const showNotificationDrawer = ref(false)
const notifications = ref([])
const openMenu = ref<number | null>(null)

const setNotificationDrawer = (isShown: boolean) => {
  showNotificationDrawer.value = isShown;
}

const fetchNotifications = async () => {
  if (localStorage.token) {
    const { data } = await ApiServiceV1.get('customer/notifications?per_page=12&page=1')
    notifications.value = data.notifications;
  }
}

function onShow(id: number) {
  openMenu.value = openMenu.value === id ? null : id
}

function onHide(id: number) {
  if (openMenu.value === id) {
    openMenu.value = null
  }
}

const freeShippingPrice = computed<number>(() => {
  if (cartStore.getTotalItemsInCart > 0) {
    return Number(cartStore.getCart?.priceUntilFreeTransport ?? 0)
  }
  const parsed = Number.parseInt(String(t('free_shipping')), 10)
  return Number.isNaN(parsed) ? 0 : parsed
})

const freeShippingProgress = computed(() => {
  if (isSignedIn.value && getCurrentCustomer?.value?.inPlusClub) {
    return 100;
  } else if (isNaN(cartStore.getCart?.priceUntilFreeTransport)) {
    return 0;
  } else if (cartStore.getCart?.priceUntilFreeTransport !== 0) {
    return (
      (Number.parseInt(t('free_shipping')) - cartStore.getCart?.priceUntilFreeTransport) /
      (Number.parseInt(t('free_shipping')) / 100)
    )
  } else return 100;
})

onMounted(() => {
  fetchNotifications()
  document.addEventListener("keydown", function (event) {
    if (event.key === "Escape") {
      showNotificationDrawer.value = false;
    }
  });
  document.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement;

    if (!target.closest('.notification-center')) {
      setNotificationDrawer(false);
    }
    if (target.closest('.show-notification-drawer')) {
      setNotificationDrawer(true);
    }
  });
});

watch(
  () => hasFreeTransport.value,
  () => { if (hasFreeTransport.value === true) freeDeliveryMsgShow.value = true }
)
</script>

<template>
  <header class="z-header bg-primary-450 top-0 left-0 sticky">
    <nav class="flex items-center pr-3 xl:pr-5">
      <div class="bg-primary pr-5 pl-4 lg:pl-6 lg:pr-7 py-3 lg:py-4 logo-wrap mr-2 order-1">
        <Logo v-once />
      </div>
      <ul class="list-unstyled mb-0 flex flex-row items-center xl:ml-0 ml-3 xl:order-2 order-3">
        <li class="xl:mr-4 mr-3 hidden xl:flex">
          <NavSearch />
        </li>
        <li
          v-if="getEventCategories.find(item => item.systemName === 'event_news')"
          class="xl:mr-5 mr-3 hidden xl:flex"
        >
          <!-- :to="{ name: 'productsPath', params: { permalink: item.permalink } }"-->
          <RouterLink
            :to="{ name: 'productsPath', params: { permalink: getEventCategories?.find(item => item.systemName === 'event_news')?.permalink } }"
            class="btn btn-wine border-0 py-2 px-4 font-bold rounded-pill relative"
          >
            {{ t('new_products') }}
            <StarsSvg
              class="absolute right-0 mr-n2 mt-n5"
              width="30"
              height="30"
            />
          </RouterLink>
        </li>
        <li class="hidden xl:flex">
          <VMenu
            theme="navItem"
            strategy="fixed"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            popper-class="navItem-max"
            :shown="openMenu === 0"
            @apply-show="onShow(0)"
            @apply-hide="onHide(0)"
          >
            <RouterLink
              v-close-popper
              :to="{ name: 'allProductsPath' }"
              class="btn bg-transparent font-bold xl:py-3 xxl:py-2 border-0 rounded-pill"
            >
              {{ t('categories') }}
            </RouterLink>
            <template #popper>
              <NavProducts />
            </template>
          </VMenu>
        </li>
        <li class="hidden xl:flex">
          <VMenu
            theme="navItem"
            strategy="fixed"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            popper-class="navItem-max"
            :shown="openMenu === 1"
            @apply-show="onShow(1)"
            @apply-hide="onHide(1)"
          >
            <button class="btn bg-transparent font-bold xl:py-3 xxl:py-2 border-0 rounded-pill">
              {{ t('mixing') }}
            </button>
            <template #popper>
              <NavOwnMix />
            </template>
          </VMenu>
        </li>
        <li class="hidden xl:flex">
          <VMenu
            theme="navItem"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            :shown="openMenu === 2"
            @apply-show="onShow(2)"
            @apply-hide="onHide(2)"
          >
            <button class="btn bg-transparent xl:py-3 xxl:py-2 border-0 rounded-pill">
              <i class="mi-smartphone icon-18 mr-1" />
              {{ t('app') }}
            </button>
            <template #popper>
              <NavAppDownload />
            </template>
          </VMenu>
        </li>
        <li>
          <VMenu
            theme="navItem"
            strategy="fixed"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            :shown="openMenu === 3"
            @apply-show="onShow(3)"
            @apply-hide="onHide(3)"
          >
            <button class="btn bg-transparent xl:py-3 xxl:py-2 border-0 rounded-pill">
              <i class="mi-bars icon-18" />
              <span class="hidden xl:block ml-1">{{ t('more') }}</span>
            </button>
            <template #popper>
              <NavMenuMore />
            </template>
          </VMenu>
        </li>
      </ul>
      <div class="hidden xl:flex items-center ml-auto order-2">
        <div>
          <VMenu
            theme="navItem"
            strategy="fixed"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            :shown="openMenu === 4"
            @apply-show="onShow(4)"
            @apply-hide="onHide(4)"
          >
            <Button
              class="btn bg-transparent p-1 border-0 rounded-pill no-decoration font-medium relative"
              aria-label="User"
              @click="isSignedIn ? router.push({ name: 'customerProfilePath' }) : openModal({ id: 'modal-sign-in' })"
            >
              <i
                v-if="isSignedIn && getCurrentCustomer?.inPlusClub"
                class="mi-mixit-plus icon-12 icon-mixit-plus absolute"
              />
              <i
                class="icon-20 sm:icon-24"
                :class="isSignedIn ? 'mi-user-fill' : 'mi-user'"
              />
              <span class="hidden xxl:block ml-1">
                {{ isSignedIn ? t('my_account') : t('sign_in') }}
              </span>
            </Button>

            <template #popper>
              <NavProfile
                :notification-amount="notifications.length"
                @show-drawer="setNotificationDrawer"
              />
            </template>
          </VMenu>
        </div>
        <div class="nav-cart">
          <VMenu
            theme="navItem"
            strategy="fixed"
            :triggers="['hover']"
            :popper-triggers="['hover']"
            :shown="openMenu === 5"
            @apply-show="onShow(5)"
            @apply-hide="onHide(5)"
          >
            <RouterLink
              :to="{ name: 'cartsPath' }"
              class="btn bg-transparent xl:py-3 xxl:py-2 border-0 rounded-pill"
              aria-describedby="tooltip-delivery"
              aria-label="Cart"
            >
              <div class="relative">
                <i
                  class="icon-20 sm:icon-24"
                  :class="getTotalItemsInCart > 0 ? 'mi-cart-fill' : 'mi-cart'"
                />
                <span
                  v-if="getTotalItemsInCart > 0"
                  class="cart-quantity-badge absolute bg-danger rounded-pill text-nowrap text-sm text-white leading-1"
                >
                  <BaseOdometer :number="getTotalItemsInCart" />
                </span>
              </div>
              <span
                v-if="isCartNotEmpty"
                class="hidden sm:block pl-1 sm:pl-2 font-semi-bold"
              >
                {{ getCartPrice }}
              </span>
            </RouterLink>
            <template #popper>
              <NavCart
                :amount="freeShippingPrice"
                :percent="freeShippingProgress"
              />
            </template>
          </VMenu>
        </div>
        <div
          v-if="isFreeTransportNotice"
          class="free-transport-notice"
        >
          <VDropdown
            theme="navItem"
            strategy="fixed"
            :auto-hide="false"
            popper-class="navItem-free-delivery hidden xl:flex"
            :shown="freeDeliveryMsgShow"
          >
            <div class="flex items-center">
              <div class="badge relative text-white items-center justify-center pl-1 pr-4">
                <i class="mi-delivery icon-28" />
              </div>
              <div class="progress-pie zindex-2 ml-n2 flex items-center justify-center">
                <strong
                  v-if="freeShippingProgress < 100"
                  class="text-white text-sm absolute"
                >
                  {{ Math.round(freeShippingProgress) }}%
                </strong>
                <i
                  v-else
                  class="mi-check icon-18 text-white absolute"
                />
                <svg
                  height="20"
                  width="20"
                  viewBox="0 0 20 20"
                >
                  <circle
                    r="5"
                    cx="10"
                    cy="10"
                    fill="transparent"
                    stroke="#4696EF"
                    stroke-width="10"
                    stroke-dasharray="32"
                    :stroke-dashoffset="32 - 0.32 * freeShippingProgress"
                    transform="rotate(-90) translate(-20)"
                  />
                </svg>
              </div>
            </div>
            <template #popper>
              <FreeTransportAlert
                :has-free-transport="hasFreeTransport"
                :is-plus-club="isSignedIn && (getCurrentCustomer?.inPlusClub || false)"
                :free-shipping-price="freeShippingPrice"
                @close-transport-alert="freeShippingMsgHandler"
              />
            </template>
          </VDropdown>
        </div>

        <VDropdown
          theme="usermenu"
          strategy="fixed"
        >
          <a
            href="#"
            class="btn p-1 border-0 rounded-pill"
            aria-label="Language option"
          >
            <Component
              :is="getCurrentLocaleFlag(country)"
              width="32"
              height="32"
            />
          </a>
          <template #popper>
            <ul class="list-unstyled m-0">
              <li
                v-for="(language, index) in LanguagesFlags"
                :key="index"
                class="col-auto px-0 ml-n1 sm:ml-1 lg:ml-0 hidden sm:block"
              >
                <a
                  v-if="language.shortcut !== country"
                  :href="language.link"
                  class="btn p-1 border-0 rounded-pill"
                  aria-label="Language option"
                >
                  <Component
                    :is="language.svg"
                    width="34"
                    height="34"
                  />
                </a>
              </li>
            </ul>
          </template>
        </VDropdown>
      </div>
      <div class="xl:hidden flex ml-auto ml-auto xl:order-3 order-2">
        <RouterLink
          class="btn bg-transparent p-1 border-0 rounded-pill no-decoration font-medium relative"
          aria-label="User"
          :to="{ name: 'customerProfilePath' }"
        >
          <i
            v-if="isSignedIn && getCurrentCustomer?.inPlusClub"
            class="mi-mixit-plus icon-12 icon-mixit-plus absolute"
          />
          <i
            class="icon-20 sm:icon-24"
            :class="isSignedIn ? 'mi-user-fill' : 'mi-user'"
          />
          <span class="ml-1 hidden md:inline ml-1">
            {{ isSignedIn ? t('my_account') : t('sign_in') }}
          </span>
        </RouterLink>
      </div>
    </nav>
  </header>
  <SpecialCategories />
  <Transition name="notification-drawer">
    <NotificationDrawer
      v-if="showNotificationDrawer"
      :notifications="notifications"
      @hide-drawer="setNotificationDrawer"
    />
  </Transition>
</template>

<style lang="scss" scoped>
.logo-wrap {
  clip-path: polygon(0 0, 100% 0, calc(100% - 1.25rem) 50%, 100% 100%, 0 100%);
}
.notification-drawer-enter-active,
.notification-drawer-leave-active {
  transition: transform 0.5s ease;
}

.notification-drawer-enter-from,
.notification-drawer-leave-to {
  transform: translateX(100%);
}
.z-header {
  z-index: 1021;
}
.free-transport-notice {
  .badge {
    background-color: #22548C;
    border-top-left-radius: 50rem;
    border-bottom-left-radius: 50rem;

    i {
      line-height: 1.2em;
      &:before {line-height: 1rem;}
    }
  }
  .progress-pie {
    background-color: #22548C;
    width: 46px;
    height: 46px;
    border-radius: 100%;
    border: 2px solid #F2B76F;

    svg {
      width: 36px;
      height: 36px;

      circle {
        transition: .5s stroke-dashoffset ease-in-out;
      }
    }
  }
}
.nav-cart {
  min-width: 138px;
  @media screen and (max-width: 1199px) {
    min-width: auto;
  }
}
.cart-quantity-badge {
  width: 20px;
  height: 20px;
  right: -10px;
  top: -8px;
}

.icon-mixit-plus {
  width: 8px;
  height: 8px;
  left: 20px;
  top: -3px;
}
</style>

<i18n>
  {
    "cs": {
    "account": "Účet",
    "app": "Aplikace",
    "categories": "Kategorie",
    "free_shipping": "1499",
    "mixing": "Mixovat",
    "more": "Více",
    "my_account": "Můj účet",
    "new_products": "Novinky",
    "sign_in": "Přihlásit se"
},
    "sk": {
    "account": "Účet",
    "app": "Aplikácia",
    "categories": "Kategórie",
    "free_shipping": "59",
    "mixing": "Mixovať",
    "more": "Viac",
    "my_account": "Môj účet",
    "new_products": "Novinky",
    "sign_in": "Prihlásiť sa"
},
    "pl": {
    "account": "Konto",
    "app": "Aplikacja",
    "categories": "Kategorie",
    "free_shipping": "199",
    "mixing": "Mieszaj",
    "more": "WIĘCEJ",
    "my_account": "Moje konto",
    "new_products": "Nowości",
    "sign_in": "Zaloguj się"
},
    "hu": {
    "account": "Fiók",
    "app": "Alkalmazás",
    "categories": "Kategóriák",
    "free_shipping": "18990",
    "mixing": "Mixelni",
    "more": "Több",
    "my_account": "Fiókom",
    "new_products": "Újdonságok",
    "sign_in": "Bejelentkezés"
},
    "de": {
    "account": "Konto",
    "app": "App",
    "categories": "Kategorie",
    "free_shipping": "49",
    "mixing": "Mixen",
    "more": "mehr",
    "my_account": "Mein Konto",
    "new_products": "Neuheiten",
    "sign_in": "Anmelden"
},
    "at": {
    "account": "Konto",
    "app": "App",
    "categories": "Kategorie",
    "free_shipping": "49",
    "mixing": "Mixen",
    "more": "mehr",
    "my_account": "Mein Konto",
    "new_products": "Neuheiten",
    "sign_in": "Anmelden"
},
    "ro": {
    "account": "Cont",
    "app": "Aplicație",
    "categories": "Categorii",
    "free_shipping": "199",
    "mixing": "Mixează",
    "more": "Mai mult",
    "my_account": "Contul meu",
    "new_products": "Noutăți",
    "sign_in": "Autentificare"
},
    "fr": {
    "account": "Compte",
    "app": "Application",
    "categories": "Catégories",
    "free_shipping": "49",
    "mixing": "Créer",
    "more": "Voir plus",
    "my_account": "Mon compte",
    "new_products": "Nouveautés",
    "sign_in": "Se connecter"
}
  }
</i18n>